/**
 * App Updates - Manage versions per platform
 */
import React, { useEffect, useState } from "react";
import { getBranding, updateVersion } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Save, Smartphone, Globe } from "lucide-react";
import { ADMIN } from "../lib/testIds";

const platforms = [
  {
    key: "android",
    label: "Android",
    icon: Smartphone,
    color: "from-emerald-500 to-teal-600",
    urlPlaceholder: "https://www.dropbox.com/scl/fi/…/ZA-Player-Pro.apk?rlkey=…&dl=1",
    urlLabel: "Dropbox APK download URL (any share link — will auto-convert to direct download)",
  },
  {
    key: "web",
    label: "Web Player",
    icon: Globe,
    color: "from-sky-500 to-indigo-500",
    urlPlaceholder: "https://app.example.com/admin/webplayer",
    urlLabel: "Web Player URL",
  },
];

function PlatformCard({ platform, config, onSave }) {
  const { toast } = useToast();
  const [version, setVersion] = useState(config?.app_versions?.[platform.key] || "1.0.0");
  const [url, setUrl] = useState(config?.update_urls?.[platform.key] || "");
  const [changelog, setChangelog] = useState(config?.changelogs?.[platform.key] || "");
  const [force, setForce] = useState(!!config?.force_update?.[platform.key]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVersion(config?.app_versions?.[platform.key] || "1.0.0");
    setUrl(config?.update_urls?.[platform.key] || "");
    setChangelog(config?.changelogs?.[platform.key] || "");
    setForce(!!config?.force_update?.[platform.key]);
  }, [config, platform.key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateVersion({
        platform: platform.key,
        version,
        update_url: url || null,
        changelog: changelog || null,
        force_update: force,
      });
      toast({ title: `${platform.label} updated to v${version}` });
      onSave();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const Icon = platform.icon;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <div
            className={`w-8 h-8 rounded-md bg-gradient-to-br ${platform.color} flex items-center justify-center`}
          >
            <Icon className="w-4 h-4 text-white" />
          </div>
          {platform.label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Latest Version</Label>
          <Input
            data-testid={ADMIN.versionInput(platform.key)}
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.0.0"
          />
        </div>
        <div>
          <Label className="text-xs">{platform.urlLabel}</Label>
          <Input
            data-testid={ADMIN.updateUrlInput(platform.key)}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={platform.urlPlaceholder}
          />
        </div>
        <div>
          <Label className="text-xs">Changelog</Label>
          <Textarea
            data-testid={ADMIN.changelogInput(platform.key)}
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            placeholder="What's new in this version..."
            rows={2}
          />
        </div>
        <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-md">
          <div>
            <Label className="text-sm">Force update</Label>
            <p className="text-xs text-slate-500">Block player until updated</p>
          </div>
          <Switch
            data-testid={ADMIN.forceUpdateSwitch(platform.key)}
            checked={force}
            onCheckedChange={setForce}
          />
        </div>
        <Button
          data-testid={ADMIN.btnSaveVersion(platform.key)}
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-orange-500 hover:bg-orange-600"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Updates() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      setConfig(await getBranding());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (loading) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <p className="text-slate-600">
        Manage app versions per platform. Players will check for updates and prompt users to upgrade.
      </p>

      {/* Built desktop client downloads */}
      <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-orange-900">Downloads</span>
          <span className="text-[10px] font-bold bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 px-1.5 py-0.5 rounded-full uppercase">
            Ready
          </span>
        </div>
        <p className="text-xs text-orange-900/80 mb-3">
          Share these direct links with your customers and resellers. Hosted on this panel — no external CDN required.
        </p>
        <div className="space-y-2">
          {[
            { name: "ZA Player Pro Web — Android APK (latest release)", file: "ZA-Player-Pro-Web-1.4.70.apk", size: "≈ 8 MB" },
            { name: "ZA Player Pro Web — Android App Bundle (Play Console upload)", file: "ZA-Player-Pro-Web-1.4.70-release.aab", size: "≈ 8 MB" },
            { name: "Admin Panel — Hot-update tarball (used by update.sh)", file: "zaplayerpro-admin-update.tar.gz", size: "≈ 1.8 MB" },
            { name: "Admin Panel — Hot updater script (update.sh)", file: "update.sh", size: "≈ 8 KB" },
          ].map((d) => {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/downloads/${d.file}`;
            return (
              <div key={d.file} className="flex items-center gap-3 bg-white border border-orange-200 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-800 truncate">{d.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">{url}</div>
                </div>
                <span className="text-[11px] text-slate-500 shrink-0">{d.size}</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-md font-medium shrink-0"
                >
                  Download
                </a>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {platforms.map((p) => (
          <PlatformCard key={p.key} platform={p} config={config} onSave={refresh} />
        ))}
      </div>
    </div>
  );
}
